export const CHUNK_RELOAD_KEY = 'chunk-reloaded';
// Long enough to stop a reload loop on an asset that is really missing, short enough that a tab
// left open across several deploys recovers from each of them.
const RELOAD_COOLDOWN_MS = 60_000;

let reloading = false;

// A deploy removes the previous build's chunks, and the host answers those URLs with the SPA page,
// so a tab still running the old build sees one of these depending on the browser.
const CHUNK_LOAD_MESSAGES = [
    'Failed to fetch dynamically imported module', // Chromium
    'error loading dynamically imported module', // Firefox
    'Importing a module script failed', // Safari
    'is not a valid JavaScript MIME type', // Safari, when the HTML fallback is served
    'Unable to preload', // Vite CSS preload
];

export function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return CHUNK_LOAD_MESSAGES.some((text) => message.includes(text));
}

export function isReloadingForStaleChunk(): boolean {
    return reloading;
}

interface ReloadOptions {
    storage: Pick<Storage, 'getItem' | 'setItem'>;
    reload: () => void;
    logger?: Pick<Console, 'error'>;
    now?: number;
}

/** Reloads into the current deploy. Returns false when a reload was just tried and did not help. */
export function reloadForStaleChunk({storage, reload, logger, now = Date.now()}: ReloadOptions): boolean {
    if (reloading) return true;
    try {
        if (now - Number(storage.getItem(CHUNK_RELOAD_KEY)) < RELOAD_COOLDOWN_MS) return false;
        // Record the attempt before reloading so a stale chunk cannot cause a reload loop.
        storage.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch (storageError) {
        logger?.error('Failed to persist chunk reload state:', storageError);
        return false;
    }
    reloading = true;
    reload();
    return true;
}

interface ChunkRecoveryOptions<T> {
    fallback: T;
    storage?: Pick<Storage, 'getItem' | 'setItem'>;
    reload?: () => void;
    logger?: Pick<Console, 'error'>;
}

export async function loadChunkWithRecovery<T>(loader: () => Promise<T>, options: ChunkRecoveryOptions<T>): Promise<T> {
    try {
        return await loader();
    } catch (error) {
        if (
            isChunkLoadError(error) &&
            options.storage &&
            options.reload &&
            reloadForStaleChunk({storage: options.storage, reload: options.reload, logger: options.logger})
        ) {
            return await new Promise<T>(() => {});
        }

        options.logger?.error('Failed to load application chunk:', error);
        return options.fallback;
    }
}
